/**
 * R-65 — Project Intent vs SED narrative reconciliation (read-only).
 */
import type { ProjectIntent } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  effectivePhases,
  effectivePower,
  withPowerRequirementMW,
} from '../projectIntent';
import { normaliseCooling, type CoolingType } from '../../types/cooling';
import { effectiveWorkloadProfile } from '../workloadProfile';

export type IntentSedField =
  | 'capacity'
  | 'phaseCount'
  | 'coolingStrategy'
  | 'nuclearInterest'
  | 'workloadProfile';

export interface Divergence {
  field: IntentSedField;
  intentValue: string;
  sedValue: string;
  sedExcerpt: string;
  severity: 'block' | 'warn';
  recommendedAction: string;
  sedSyncValue?: unknown;
}

export interface IntentSedReconciliationResult {
  divergences: Divergence[];
  aligned: boolean;
}

export interface GateDecision {
  pass: boolean;
  blockingDivergences: Divergence[];
}

export class IntentSedMismatchError extends Error {
  readonly result: IntentSedReconciliationResult;

  constructor(result: IntentSedReconciliationResult) {
    const fields = result.divergences
      .filter((d) => d.severity === 'block')
      .map((d) => d.field)
      .join(', ');
    super(`Intent/SED reconciliation blocked dispatch${fields ? `: ${fields}` : ''}`);
    this.name = 'IntentSedMismatchError';
    this.result = result;
  }
}

const CAPACITY_BLOCK_TOLERANCE_PCT = 25;

const COOLING_KEYWORDS: Record<CoolingType, RegExp[]> = {
  'air-dry-cooler': [/\bair[- ]?cooled\b/i, /\bdry[- ]?cooling\b/i, /\bair cooling\b/i],
  evaporative: [/\bevaporative\b/i, /\bcooling tower\b/i, /\bwater[- ]?cooled\b/i, /\bonce[- ]?through\b/i],
  'closed-loop-liquid': [/\bliquid[- ]?cooled\b/i, /\bdirect[- ]?to[- ]?chip\b/i, /\bCDU\b/i, /\bliquid cooling\b/i],
  hybrid: [/\bhybrid\s+cooling\b/i, /\bhybrid\b/i],
  undecided: [],
};

const WORKLOAD_KEYWORDS: Record<string, RegExp[]> = {
  'ai-training': [/\bAI training\b/i, /\btraining cluster\b/i, /\bGPU training\b/i],
  'ai-inference': [/\bAI inference\b/i, /\binference\b/i, /\bLLM serving\b/i],
  hpc: [/\bHPC\b/i, /\bhigh[- ]performance computing\b/i],
  enterprise: [/\benterprise\b/i, /\bcorporate datacenter\b/i],
  colocation: [/\bcolocation\b/i, /\bmulti[- ]tenant\b/i, /\bwholesale colo\b/i],
  edge: [/\bedge datacenter\b/i, /\bedge compute\b/i],
  mixed: [/\bmixed[- ]workload\b/i, /\bhybrid[- ]workload\b/i],
};

function sedNarrativeText(sed: SiteEvidenceDocument): string {
  const parts: string[] = [];
  if (sed.siteContext?.text) parts.push(sed.siteContext.text);
  for (const claim of sed.siteContext?.claims ?? []) {
    const claimValue = claim.claimedValue ?? claim.claim;
    if (claimValue) parts.push(String(claimValue));
  }
  return parts.join('\n');
}

function resolveSedIntent(sed: SiteEvidenceDocument): ProjectIntent | undefined {
  return sed.fullProjectIntent;
}

function compareCapacity(intent: ProjectIntent, sedIntent: ProjectIntent | undefined): Divergence | null {
  const intentPower = effectivePower(intent);
  const sedPower = effectivePower(sedIntent);
  const intentMw = intentPower.mw ?? null;
  const sedMw = sedPower.mw ?? null;
  if (intentMw == null || sedMw == null) return null;
  if (intentMw === sedMw) return null;
  const deltaPct = Math.abs(sedMw - intentMw) / Math.max(intentMw, 1) * 100;
  const severity: 'block' | 'warn' = deltaPct > CAPACITY_BLOCK_TOLERANCE_PCT ? 'block' : 'warn';
  return {
    field: 'capacity',
    intentValue: intentPower.displayLabel,
    sedValue: sedPower.displayLabel,
    sedExcerpt: `SED fullProjectIntent powerRequirementMW=${sedMw}`,
    severity,
    recommendedAction: severity === 'block'
      ? 'Align Project Intent capacity with SED snapshot or document override rationale.'
      : 'Review capacity delta before dispatch.',
    sedSyncValue: withPowerRequirementMW(sedIntent ?? intent, sedMw).powerRequirementMW,
  };
}

function detectCoolingInText(text: string, preferred?: CoolingType): CoolingType | null {
  if (preferred && preferred !== 'undecided' && (COOLING_KEYWORDS[preferred] ?? []).some((re) => re.test(text))) {
    return preferred;
  }
  for (const [type, patterns] of Object.entries(COOLING_KEYWORDS) as Array<[CoolingType, RegExp[]]>) {
    if (type === 'undecided') continue;
    if (patterns.some((re) => re.test(text))) return type;
  }
  return null;
}

function compareCooling(intent: ProjectIntent, sed: SiteEvidenceDocument, sedIntent: ProjectIntent | undefined): Divergence | null {
  const intentCooling = normaliseCooling(intent.coolingStrategy);
  const sedCooling = normaliseCooling(sedIntent?.coolingStrategy ?? 'undecided');
  const narrative = sedNarrativeText(sed);
  const narrativeCooling = detectCoolingInText(narrative, intentCooling);
  const effectiveSed = narrativeCooling ?? (sedCooling === 'undecided' ? null : sedCooling);
  if (!effectiveSed || effectiveSed === intentCooling) return null;
  return {
    field: 'coolingStrategy',
    intentValue: intentCooling,
    sedValue: effectiveSed,
    sedExcerpt: narrative.slice(0, 160) || `SED coolingStrategy=${sedCooling}`,
    severity: 'warn',
    recommendedAction: 'Confirm cooling strategy in Project Intent matches SED narrative.',
    sedSyncValue: effectiveSed,
  };
}

function compareWorkload(intent: ProjectIntent, sed: SiteEvidenceDocument): Divergence | null {
  const intentWp = effectiveWorkloadProfile(intent);
  const narrative = sedNarrativeText(sed);
  if ((WORKLOAD_KEYWORDS[intentWp] ?? []).some((re) => re.test(narrative))) {
    return null;
  }
  for (const [profile, patterns] of Object.entries(WORKLOAD_KEYWORDS)) {
    if (profile === intentWp) continue;
    if (patterns.some((re) => re.test(narrative))) {
      return {
        field: 'workloadProfile',
        intentValue: intentWp,
        sedValue: profile,
        sedExcerpt: narrative.slice(0, 160),
        severity: 'warn',
        recommendedAction: 'Review workload profile divergence between Intent and SED narrative.',
      };
    }
  }
  return null;
}

function comparePhaseCount(intent: ProjectIntent, sedIntent: ProjectIntent | undefined): Divergence | null {
  const intentPhases = effectivePhases(intent);
  const sedPhases = effectivePhases(sedIntent ?? intent);
  if (intentPhases.phaseCount === sedPhases.phaseCount) return null;
  return {
    field: 'phaseCount',
    intentValue: String(intentPhases.phaseCount),
    sedValue: String(sedPhases.phaseCount),
    sedExcerpt: `SED phase count ${sedPhases.phaseCount}`,
    severity: 'warn',
    recommendedAction: 'Align phase count between Project Intent and SED.',
  };
}

function compareNuclear(intent: ProjectIntent, sedIntent: ProjectIntent | undefined): Divergence | null {
  const a = intent.nuclearInterest ?? 'no';
  const b = sedIntent?.nuclearInterest ?? a;
  if (a === b) return null;
  return {
    field: 'nuclearInterest',
    intentValue: a,
    sedValue: b,
    sedExcerpt: `SED nuclearInterest=${b}`,
    severity: 'warn',
    recommendedAction: 'Confirm nuclear interest alignment.',
  };
}

export function reconcileIntentVsSed(
  intent: ProjectIntent,
  sed: SiteEvidenceDocument,
): IntentSedReconciliationResult {
  const sedIntent = resolveSedIntent(sed);
  const divergences: Divergence[] = [];

  const capacity = compareCapacity(intent, sedIntent);
  if (capacity) divergences.push(capacity);

  const cooling = compareCooling(intent, sed, sedIntent);
  if (cooling) divergences.push(cooling);

  const workload = compareWorkload(intent, sed);
  if (workload) divergences.push(workload);

  const phases = comparePhaseCount(intent, sedIntent);
  if (phases) divergences.push(phases);

  const nuclear = compareNuclear(intent, sedIntent);
  if (nuclear) divergences.push(nuclear);

  return {
    divergences,
    aligned: divergences.length === 0,
  };
}

export function dispatchGate(result: IntentSedReconciliationResult): GateDecision {
  const blockingDivergences = result.divergences.filter((d) => d.severity === 'block');
  return {
    pass: blockingDivergences.length === 0,
    blockingDivergences,
  };
}

export function buildIntentSyncPatches(
  intent: ProjectIntent,
  result: IntentSedReconciliationResult,
): Partial<ProjectIntent> {
  const patch: Partial<ProjectIntent> = {};
  for (const d of result.divergences) {
    if (d.field === 'capacity' && typeof d.sedSyncValue === 'number') {
      Object.assign(patch, withPowerRequirementMW(intent, d.sedSyncValue));
    }
    if (d.field === 'coolingStrategy' && typeof d.sedSyncValue === 'string') {
      patch.coolingStrategy = d.sedSyncValue as ProjectIntent['coolingStrategy'];
    }
  }
  return patch;
}

export function applyIntentSedDispatchGate(
  intent: ProjectIntent,
  sed: SiteEvidenceDocument,
): { intent: ProjectIntent; result: IntentSedReconciliationResult; gate: GateDecision } {
  const result = reconcileIntentVsSed(intent, sed);
  const gate = dispatchGate(result);
  if (!gate.pass) {
    throw new IntentSedMismatchError(result);
  }
  return { intent, result, gate };
}
